import { useRouter } from 'next/router';
import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { useSelector } from 'react-redux';

import { api } from '../lib/api/client';
import { RootState } from '../store';

/**
 * TenantContext:
 * - All network calls use apiFetch (runtime base + auth/subdomain headers).
 * - Avoids absolute URLs; works in dev (3001→3000) and prod (3011→:3010 via rewrite).
 */

interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  customDomain?: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  subscriptionTier: string;
}

interface TenantContextType {
  tenant: Tenant | null;
  loading: boolean;
  updateTenant: (tenantData: Partial<Tenant>) => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>({
  tenant: null,
  loading: true,
  updateTenant: async () => {},
});

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  // During SSR, provide static context to prevent hook usage
  if (typeof window === 'undefined') {
    const ssrValue = {
      tenant: null,
      loading: false, // Not loading during SSR
      updateTenant: async () => {}, // No-op during SSR
    };
    return <TenantContext.Provider value={ssrValue}>{children}</TenantContext.Provider>;
  }

  // Client-side only: use hooks normally
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const token = useSelector((state: RootState) => state.auth.token);
  const user = useSelector((state: RootState) => state.auth.user);
  const isAuthenticated = !!token;
  const router = useRouter();
  const pathname = router.pathname;

  useEffect(() => {
    const fetchTenant = async () => {
      const publicRoutes = ['/', '/login', '/register', '/forgot-password', '/reset-password'];
      if (publicRoutes.includes(pathname)) {
        setTenant(null);
        setLoading(false);
        return;
      }
      // FAANG-GRADE: Role-based context loading - only partner admins and super admins should load tenant data
      const allowedRoles = ['partner_admin', 'super_admin'];
      const hasTenantAccess = user?.role && allowedRoles.includes(user.role);

      // Only fetch tenant data for users who belong to a tenant AND have tenant access roles
      const isPlatformUser = !user?.tenantId || user.tenantId === 'null' || user.tenantId === null;
      if (isAuthenticated && !isPlatformUser && hasTenantAccess && token) {
        setLoading(true);
        try {
          const data: any = await api.get(`/api/tenants/${user.tenantId}`);
          setTenant(data);
        } catch (err: any) {
          if ([401, 403, 404].includes(err?.status)) {
            if (err?.status === 404) {
              console.warn('Session expired: /api/tenants/:id returned 404');
            }
            router.push('/login');
          } else {
            setTenant(null);
          }
        } finally {
          setLoading(false);
        }
      } else {
        setTenant(null);
        setLoading(false);
      }
    };

    fetchTenant();
  }, [isAuthenticated, user, token, pathname, router]);

  const updateTenant = async (tenantData: Partial<Tenant>) => {
    if (!isAuthenticated || !user?.tenantId || !token) {
      throw new Error('Not authenticated');
    }

    const updated = await api.put(`/api/tenants/${user.tenantId}`, tenantData);

    setTenant(updated);
    return updated;
  };

  const value = {
    tenant,
    loading,
    updateTenant,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  // Context now has safe defaults, so it should never be undefined
  if (!context) {
    console.error('[useTenant] Context is null or undefined, this should not happen');
    return {
      tenant: null,
      loading: true,
      updateTenant: async () => {},
    };
  }
  return context;
};
